import { createContext, useCallback, useEffect, useRef } from "react"

import { useAppKitTheme } from "@reown/appkit/react"
import { useLocalStorage } from "usehooks-ts"

export interface ThemeProviderProps {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

export interface ThemeProviderState {
  theme: Theme
  setTheme: (theme: Theme) => void
  /** Set to true before calling setTheme when this tab handles the transition (e.g. ThemeToggle). */
  skipNextTransitionRef: { current: boolean }
}

export type Theme = "dark" | "light" | "system"

const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null,
  skipNextTransitionRef: { current: false },
}

export const ThemeProviderContext =
  createContext<ThemeProviderState>(initialState)

function deserializeTheme(value: string): Theme {
  return value === "dark" || value === "light" || value === "system"
    ? value
    : "dark"
}

export function ThemeProvider({
  children,
  defaultTheme = "dark",
  storageKey = "vite-ui-theme",
  ...props
}: ThemeProviderProps) {
  const [theme, setThemeState] = useLocalStorage<Theme>(
    storageKey,
    defaultTheme,
    {
      serializer: (v) => v,
      deserializer: deserializeTheme,
    }
  )
  const { setThemeMode } = useAppKitTheme()
  const skipNextTransitionRef = useRef(false)
  const isInitialMount = useRef(true)

  // Sync theme to Appkit on mount so wallet modal matches app theme from the start
  useEffect(() => {
    const resolved =
      theme === "system"
        ? (typeof window !== "undefined" &&
          window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light")
        : theme
    setThemeMode(resolved === "dark" ? "dark" : "light")
  }, [theme, setThemeMode])

  useEffect(() => {
    const root = window.document.documentElement

    const applyTheme = () => {
      root.classList.remove("light", "dark")
      if (theme === "system") {
        const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
          .matches
          ? "dark"
          : "light"
        root.classList.add(systemTheme)
      } else {
        root.classList.add(theme)
      }
    }

    const skipTransition =
      skipNextTransitionRef.current || isInitialMount.current
    if (skipNextTransitionRef.current) {
      skipNextTransitionRef.current = false
    }
    if (isInitialMount.current) {
      isInitialMount.current = false
    }

    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    const hasViewTransition =
      typeof document !== "undefined" &&
      typeof document.startViewTransition === "function"

    if (!skipTransition && hasViewTransition && !prefersReducedMotion) {
      document.startViewTransition(applyTheme)
    } else {
      applyTheme()
    }
  }, [theme])

  const setTheme = useCallback(
    (newTheme: Theme) => {
      setThemeState(newTheme)
      const resolved =
        newTheme === "system"
          ? (typeof window !== "undefined" &&
            window.matchMedia("(prefers-color-scheme: dark)").matches
            ? "dark"
            : "light")
          : newTheme
      setThemeMode(resolved === "dark" ? "dark" : "light")
    },
    [setThemeState, setThemeMode]
  )

  const value = {
    theme,
    setTheme,
    skipNextTransitionRef,
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}
